/**
 *
 * @page 企业中心/数据看板/管理员/巡检质量统计
 *
 **/

import dayjs from 'dayjs';
import { useReducer } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useStoreState } from '&/utils/customHooks';
import { observer } from 'mobx-react';
import BasePageWrap from '&/components/ResourceLib/BasePageWrap';
import { Button, Form, Table, Space, message, Tooltip } from 'antd';
import { Select as AntSelect } from '&/components/AntdEncapsulation';
import {
    getManegers,
    getAbnormalList,
    getAbnormalDetail,
} from '@/api/personCenter/dataBoard';
import OrganizationSelect from '@/components/UserCenter/OrganizationSelect';
import BaseDrawer from '&/components/BaseDrawer';
import { DAY_OPTIONS, downloadExcel } from './constants';
import { getSelectedManegerLabel, getSelectedDayLabel } from './common';
import './AbnormalList.scss';

function AbnormalList() {
    const isAdmin = !useRouteMatch({
        path: '/user-center/manager/satisfaction-list',
        strict: false, // 全路径?
        sensitive: true, // 大小写?
    });

    const { search } = useLocation();
    const urlParams = new URLSearchParams(search);
    const initManager = urlParams.get('manager');
    const initDays = urlParams.get('days');
    const { entId } = useStoreState('dataBoard');
    const [result, setResult] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const dftParams = {
        // page: 1,
        // pageNum: 10,
        days: initDays !== null ? +initDays : 30,
        managerId: initManager,
        isAll: 1,
        export: 0,
    };

    if (isAdmin) dftParams.entId = entId;

    const [queryForm, setQueryForm] = useState(dftParams);

    const [didUpdate, forceUpdate] = useReducer(s => s + 1, 0);

    // 初始化调用和entId更新调用
    useEffect(() => {
        if (isAdmin) {
            setQueryForm({
                ...queryForm,
                entId,
                isAll: !entId ? 1 : 2,
            });

            setDetailParams(prev => ({
                ...prev,
                entId,
                isAll: !entId ? 1 : 2,
            }));
        }
        forceUpdate();
    }, [entId]);

    useEffect(() => {
        queryManagerOptions(queryForm);
    }, [queryForm.entId]);

    const onParamChange = (val, key) => {
        setQueryForm(prev => ({ ...prev, [key]: val, page: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const getExcelFileName = () => {
        return (
            '巡检异常统计' +
            '-' +
            getSelectedManegerLabel(managerOpts, queryForm.managerId) +
            '-' +
            getSelectedDayLabel(DAY_OPTIONS(intl), queryForm.days) +
            '-' +
            dayjs().format('YYYY_MM_DD')
        );
    };

    const queryList = async () => {
        const PAYLOAD = { ...queryForm };
        !PAYLOAD.entId && delete PAYLOAD.entId;
        const { export: isExport } = PAYLOAD;
        try {
            !isExport && setIsLoading(true);
            const res = await getAbnormalList(PAYLOAD);
            if (isExport) {
                downloadExcel(res, getExcelFileName()).catch(err => {
                    if (err.code === 0) {
                        message.warn('无内容');
                    } else {
                        message.warn(err?.msg);
                    }
                });
            } else {
                const { data } = res;
                const list = (data || []).map(item => {
                    item.stageName = item['_id'].stageName;
                    return item;
                });

                setResult(list);
            }
        } finally {
            setIsLoading(false);
            isExport && setQueryForm(prev => ({ ...prev, export: 0 }));
        }
    };

    useEffect(() => {
        didUpdate > 0 && queryList();
    }, [didUpdate]);

    const dftManeger = {
        value: null,
        label: intl.get('base.all'),
    };
    const [managerOpts, setManagerOpts] = useState([dftManeger]);

    function queryManagerOptions(payload) {
        const PARAMS = {
            isAll: payload.isAll,
            roleId: 2, // 项目经理
        };

        if (payload.entId) {
            PARAMS.entId = payload.entId;
        }
        getManegers(PARAMS).then(({ data }) => {
            const opts = [dftManeger];
            for (const el of data.list) {
                opts.push({
                    value: el.userId,
                    label: el.username,
                });
            }
            setManagerOpts(opts);
        });
    }

    const [visible, setVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const allProject = { value: null, label: '全部' };
    const [detailParams, setDetailParams] = useState({
        projectName: allProject,
        isAll: 1,
        managerId: null,
    });

    const onProjectNameChange = projectName => {
        setDetailParams(prev => ({ ...prev, projectName }));
    };

    useEffect(() => {
        if (!visible) {
            initParams();
        }
    }, [visible]);

    useEffect(() => {
        visible && queryDetail();
    }, [detailParams]);

    const [detaileTitle, setDetaileTitle] = useState('');

    const [projectNameOpts, setProjectNameOpts] = useState([allProject]);

    function initParams() {
        setDetailParams({
            projectName: allProject,
            isAll: 1,
            managerId: null,
        });
        setProjectNameOpts([]);
    }

    const showDetail = stageName => {
        setVisible(true);
        setDetailParams(prev => ({
            ...prev,
            stageName,
            days: queryForm.days,
        }));
        setDetaileTitle(`${stageName}-巡检异常统计`);
    };

    function queryDetail() {
        setDetailLoading(true);
        const PARAMS = { ...detailParams };
        PARAMS.projectName = detailParams.projectName?.label;
        if (!detailParams.projectName || !detailParams.projectName.value) {
            PARAMS.projectName = '';
        }
        getAbnormalDetail(PARAMS)
            .then(({ data }) => {
                setDetailData(data);
                // 根据projectId去重
                if (projectNameOpts.length) return;
                const result = Object.values(
                    data.reduce((acc, cur) => {
                        const { projectId } = cur;
                        acc[projectId] = {
                            value: projectId,
                            label: cur.projectName,
                        };
                        return acc;
                    }, {})
                );
                setProjectNameOpts([allProject].concat(result));
            })
            .finally(() => {
                setDetailLoading(false);
            });
    }

    const onClose = () => {
        setVisible(false);
    };

    const { Item } = Form;

    const subColumns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 60,
            align: 'center',
            render: (_, __, idx) => {
                return idx + 1;
            },
        },
        {
            title: '项目名称',
            dataIndex: 'projectName',
            width: 100,
            align: 'center',
        },
        {
            title: '计划巡检次数',
            dataIndex: 'planNum',
            width: 120,
            align: 'center',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '实际巡检次数',
            dataIndex: 'actualNum',
            width: 120,
            align: 'center',
        },
        {
            title: '项目经理',
            dataIndex: 'manager',
            align: 'center',
            width: 100,
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '手机号码',
            dataIndex: 'managerMobile',
            align: 'center',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
    ];

    const detailModalEl = (
        <BaseDrawer
            visible={visible}
            title={detaileTitle}
            destroyOnClose
            bodyStyle={{
                padding: '1.375rem 1.25rem',
            }}
            className="inspection-item-detail"
            onClose={onClose}>
            <Space>
                <Item label={'项目名称'}>
                    <AntSelect
                        labelInValue
                        value={detailParams.projectName}
                        onChange={onProjectNameChange}
                        options={projectNameOpts}
                    />
                </Item>
                <Item label={'项目经理'}>
                    <AntSelect
                        onChange={e =>
                            setDetailParams(prev => ({
                                ...prev,
                                managerId: e,
                            }))
                        }
                        className="manager-select"
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={managerOpts}
                        value={detailParams.managerId}
                    />
                </Item>
                <Item label={'时间区间'}>
                    <AntSelect
                        value={detailParams.days}
                        onChange={e =>
                            setDetailParams(prev => ({
                                ...prev,
                                days: e,
                            }))
                        }
                        options={DAY_OPTIONS(intl)}
                    />
                </Item>
            </Space>

            <Table
                className="normal-table"
                loading={detailLoading}
                bordered
                columns={subColumns}
                dataSource={detailData}
                scroll={{
                    y: 'calc(100vh - 12.4rem)',
                }}
                size="small"
                pagination={false}
            />
        </BaseDrawer>
    );

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 70,
            align: 'center',
            render: (_, __, idx) => {
                return (
                    idx + 1 /*  + (queryForm.page - 1) * queryForm.pageNum */
                );
            },
        },
        {
            title: '施工阶段',
            dataIndex: 'stageName',
        },
        {
            title: '异常次数',
            dataIndex: 'exceptionNum',
            render: (exceptionNum, row) => {
                return (
                    <span
                        className="native-link pointer"
                        onClick={() => showDetail(row.stageName)}>
                        {exceptionNum}
                    </span>
                );
            },
        },
        {
            title: '异常项目数',
            dataIndex: 'projectNum',
        },
        {
            title: '项目经理',
            dataIndex: 'manager',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '更新时间',
            dataIndex: 'checkDate',
            align: 'center',
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];

    const onExoprt = () => {
        setQueryForm(prev => ({ ...prev, export: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const addBtn = (
        <Button type="primary" onClick={onExoprt} disabled={!result.length}>
            导出表单
        </Button>
    );

    return (
        <>
            {isAdmin && <OrganizationSelect />}
            <BasePageWrap
                title={<strong>巡检异常统计</strong>}
                className="inspection-list"
                type="card"
                headerRight={addBtn}>
                <header className="member-list-form">
                    <Item label={'项目经理'}>
                        <AntSelect
                            onChange={e => onParamChange(e, 'managerId')}
                            className="manager-select"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={managerOpts}
                            value={queryForm.managerId}
                        />
                    </Item>
                    <Item label={'时间区间'}>
                        <AntSelect
                            value={queryForm.days}
                            onChange={e => onParamChange(e, 'days')}
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </header>

                <Table
                    rowKey="checkDate"
                    className="normal-table"
                    loading={isLoading}
                    bordered
                    columns={columns}
                    dataSource={result}
                    size="small"
                    pagination={false} /* {
                        size: 'small',
                        current: queryForm.page,
                        onChange: handlePageChange,
                        defaultPageSize: queryForm.pageNum,
                        total: result.total,
                        showSizeChanger: false,
                        style: { marginBottom: 0 },
                        showTotal: total => intl.get('base.total', { total }),
                    } */
                />
            </BasePageWrap>
            {detailModalEl}
        </>
    );
}

export default observer(AbnormalList);
